import { Box, Typography } from "@mui/material";

import { format } from "date-fns";
import { GroupBy } from "gql/graphql";

const COL_WIDTH = 25; //px

interface ChartColumnProps {
  percentage: number;
  day: string | number;
  count: number;
  isWeekend: boolean;
  hide: boolean;
  color?: string;
}

export const ChartColumn = ({
  isWeekend,
  color,
  percentage,
  day,
  count,
  hide,
}: ChartColumnProps) => {
  const WIDTH = "50%";
  return (
    <Box
      sx={() => ({
        height: 180,
        width: WIDTH,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
      })}
    >
      {hide ? (
        <Box sx={{ flex: 1 }} />
      ) : (
        <>
          {count > 0 ? (
            <Typography variant="body1" fontWeight={500} mb={2}>
              {count}
            </Typography>
          ) : (
            <Box
              sx={{
                minHeight: "20px",
                mb: 2,
              }}
            />
          )}
          <Box
            sx={{
              height: "100%",
              width: WIDTH,
              backgroundColor: "rgba(24, 28, 50, 0.05)",
              borderRadius: 100,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={(theme) => ({
                position: "absolute",
                top: `${100 - percentage}%`,
                height: `100%`,
                width: "100%",
                backgroundColor: color ?? theme.palette.success.main,
                borderRadius: "inherit",
              })}
            />
          </Box>
        </>
      )}
      <Typography
        variant="body2"
        color={(theme) => theme.palette.grey[isWeekend ? 900 : 600]}
        mt={2}
      >
        {day}
      </Typography>
    </Box>
  );
};
// We dont currently have a use for this, but probably will in the future
void ChartColumn;

interface DualNumberChartColumnProps {
  isWeekend?: boolean;
  upperColor: string;
  lowerColor: string;
  totalCount: number;
  upperCount: number;
  lowerCount: number;
  percentage: number;
  day: string | number;
  month: number;
  hide: boolean;
  groupBy?: GroupBy;
  headerColor?: string;
  header?: string;
  sx?: any;
}

export const DualNumberChartColumn = ({
  isWeekend = false,
  upperColor,
  lowerColor,
  totalCount,
  upperCount,
  lowerCount,
  percentage,
  day,
  month,
  hide,
  groupBy = GroupBy.Day,
  headerColor,
  header,
  sx,
}: DualNumberChartColumnProps) => {
  return (
    <Box
      sx={() => ({
        height: 200,
        width: `${COL_WIDTH}px`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        marginLeft: `${COL_WIDTH / 2.5}px`,
        marginRight: `${COL_WIDTH / 2.5}px`,
        ...sx,
      })}
    >
      {hide ? (
        <Box sx={{ flex: 1 }} />
      ) : (
        <>
          {header ? (
            <Typography variant="body1" fontWeight={500} color={headerColor}>
              {header}
            </Typography>
          ) : (
            <Box
              sx={{
                minHeight: "20px",
              }}
            />
          )}

          {upperCount || lowerCount ? (
            <Typography variant="body1" fontWeight={500} color={lowerColor}>
              {upperCount}
            </Typography>
          ) : (
            <Box
              sx={{
                minHeight: "20px",
              }}
            />
          )}

          {lowerCount || upperCount ? (
            <Typography variant="body1" fontWeight={500} color={upperColor}>
              {lowerCount}
            </Typography>
          ) : (
            <Box
              sx={{
                minHeight: "20px",
              }}
            />
          )}

          <Typography variant="body1" fontWeight={500}>
            {totalCount}
          </Typography>

          <Box
            sx={{
              height: "100%",
              width: COL_WIDTH,
              backgroundColor: "rgba(24, 28, 50, 0.05)",
              borderRadius: 100,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={() => ({
                position: "absolute",
                top: `${100 - ((upperCount + lowerCount) / (totalCount ? totalCount : 1)) * percentage}%`,
                height: `100%`,
                width: "100%",
                backgroundColor: lowerColor,
                borderRadius: "inherit",
              })}
            />
            <Box
              sx={() => ({
                position: "absolute",
                top: `${100 - (lowerCount / (totalCount ? totalCount : 1)) * percentage}%`,
                height: `100%`,
                width: "100%",
                backgroundColor: upperColor,
                borderRadius: "inherit",
              })}
            />
          </Box>
        </>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          variant="body2"
          color={(theme) => theme.palette.grey[isWeekend ? 400 : 900]}
          mt={2}
          sx={{ textAlign: "center" }}
        >
          {day}
        </Typography>
        {groupBy === GroupBy.Day && (
          <Typography
            variant="body2"
            color={(theme) => theme.palette.grey[isWeekend ? 400 : 900]}
          >
            {format(new Date(2000, month), "MMM")}
          </Typography>
        )}
      </div>
    </Box>
  );
};
